import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Popover, Typography } from "@material-ui/core";
import clsx from "clsx";
import {
  ActionLinks,
  BannerSeverity,
  IActionLink,
  Icon,
  IconName,
  NotificationBanner,
  StatusTag,
  getTwoLineAddresses,
  CorporateButton,
} from "halifax";
import React, { useCallback, useRef, useState } from "react";
import { RouteComponentProps, Link } from "react-router-dom";
import {
  CFAR_CONTRACT_TYPE,
  HOTELS_CLICKED_CHANGE_RESERVATION,
  HotelBookAgainSourceEnum,
  HotelConfirmationCodeEnum,
  HotelItineraryState,
  HOTEL_BOOK_AGAIN,
  LodgingAddressExact,
  LodgingCollectionEnum,
  MyTripsFilter,
  MyTripsModalTypes,
  TagColors,
} from "redmond";

import { isCorpTenant, useIsTravelApp } from "@capone/common";
import { useExperimentsById } from "@capone/experiments";
import { HOW_REFUNDS_WORK_LINK, HOW_REFUNDS_WORK_TEXT } from "../../constants";
import { ConfirmationSummary } from "../ConfirmationSummary";
import { getLabels, getValues } from "../FlightCard/helpers";
import {
  addHotelType,
  getPostalCode,
} from "../HotelCard/components/DesktopHotelCard/component";
import {
  HotelCancellation,
  HotelCardRoomTypeContent,
  HotelCheckInInstructions,
} from "../HotelCard/components/HotelInfoParts";
import { HotelMap } from "../HotelCard/components/HotelMap";
import * as textConstants from "../HotelCard/constants";
import { MobileActionLinks } from "../MobileActionLinks";
import { PaymentSummary } from "../PaymentSummary";
import { SummaryCard } from "../SummaryCard";
import { TravelersSummary } from "../TravelersSummary";
import { MobileHotelItineraryDetailsConnectorProps } from "./container";
import { TripDescriptionSummary } from "../TripDescriptionSummary";

import {
  ActiveExperiments,
  useExperiment,
} from "../../../../../../context/experiments";
import { getHotelStayDuration } from "../../../../utils/constants";
import { PremierCollectionBenefits } from "../HotelCard/components/HotelInfoParts/component";
import "./styles.scss";
import { AddOn, AddOnEnum, AddOnsSummary } from "../AddOnsSummary";
import {
  calculateHotelTotals,
  getCurrencyString,
} from "../../../../../../utils/helpers";
import { config } from "../../../../../../api/config";
import { getBookAgainUrl } from "../../../../../../utils/bookingHelpers";
import { trackEvent } from "../../../../../../api/v1/analytics/trackEvent";
import {
  hasCfarExpired,
  isNegativePremiumCfar,
  shouldShowCfarActionButton,
} from "../HotelCard/helpers/cfarHelpers";

export interface IMobileHotelItineraryDetailsProps
  extends RouteComponentProps,
    MobileHotelItineraryDetailsConnectorProps {}

export const MobileHotelItineraryDetails = ({
  hotel,
  history,
  setOpenModal,
  setSelectedHotel,
  tripsFilter,
}: IMobileHotelItineraryDetailsProps) => {
  const refundPopoverRef = useRef<HTMLButtonElement>(null);
  const [refundPopoverOpen, setRefundPopoverOpen] = useState(false);

  if (!hotel) {
    return null;
  }

  const { isTravelApp } = useIsTravelApp();

  const { ancillaries, tenant } = hotel;

  // note: destructuring can be expanded in the future as we have more ancillaries
  const { cfar } = ancillaries;

  const { title, confirmationCode, startDate, endDate, subtitle } =
    textConstants.getHotelInfoDetails(hotel, true);
  const nights = getHotelStayDuration(hotel.reservation);
  const forceCfarErrorModalEnabled = useExperiment(
    "corp-force-cfar-error-modal",
  );
  const onOpenModal = (type: MyTripsModalTypes) =>
    setOpenModal({
      type,
      selectedItinerary: addHotelType(hotel),
    });
  const resState = hotel.reservation?.state?.ReservationState;
  const itemizedReceiptExp = useExperiment(
    ActiveExperiments.SelfServeHotelItemizedReceipt,
  );
  const hotelBookAgainEnabled =
    useExperimentsById("corp-hotel-book-again")?.variant === "available";
  const isExpenseManagementEnabled =
    useExperimentsById("corp-expense-management")?.variant === "available";
  const { tripDescription } = hotel;
  const isCancelled = [
    HotelItineraryState.Canceled,
    HotelItineraryState.CancellationFailure,
    HotelItineraryState.Cancelled,
  ].includes(resState);
  const isPastTrips = tripsFilter === MyTripsFilter.PAST_TRIPS;

  const actions: IActionLink[] = [];
  const topActions: IActionLink[] = []; // render at the top of the page

  const closeRefundPopover = () => setRefundPopoverOpen(false);
  const openRefundPopover = () => setRefundPopoverOpen(true);

  const hasCfar = cfar != null;
  const isNegativePremium = isNegativePremiumCfar(cfar);

  // note: hasAddOnToDisplay can be expanded in the future as we have more features added in
  const hasAddOnToDisplay = hasCfar && !isNegativePremium;

  const shouldOpenErrorModal =
    forceCfarErrorModalEnabled && !!tenant && config.TENANT !== tenant;

  // As we'll only be showing the CFAR action button for expired CFAR policies if they are negative premium CFAR policies,
  // we will only open the expired modal those three conditions are met.
  // With HTS CFARs, expired contracts will be handled in the iframe
  const isLegacyContract =
    cfar?.contractType?.ContractType !== CFAR_CONTRACT_TYPE.HTS;
  const shouldOpenExpiredModal =
    isLegacyContract && hasCfarExpired(cfar) && isNegativePremium;

  const handleOpenCfar = useCallback(() => {
    if (shouldOpenErrorModal) {
      onOpenModal(MyTripsModalTypes.CfarHotelError);
    } else if (shouldOpenExpiredModal) {
      onOpenModal(MyTripsModalTypes.CfarHotelExpired);
    } else {
      onOpenModal(MyTripsModalTypes.CfarHotel);
    }
  }, [onOpenModal, shouldOpenErrorModal, shouldOpenExpiredModal]);

  const showCfarButton = shouldShowCfarActionButton(cfar);

  if (!isCancelled && !isPastTrips) {
    actions.push({
      content: "Change hotel reservation",
      onClick: () => {
        trackEvent({
          eventName: HOTELS_CLICKED_CHANGE_RESERVATION,
          properties: {
            reservation_id: hotel?.reservation?.reservationId,
            reservation_booking_id: hotel?.reservation?.reservationBookingId,
            lodging_id: hotel?.reservation?.lodgingData.id,
            check_in_date: hotel?.reservation?.checkInDate,
          },
          encryptedProperties: [
            hotel?.reservation?.trackingPropertiesV2.encryptedProperties ?? "",
          ],
        });
        onOpenModal(MyTripsModalTypes.ChangeHotel);
      },
    });

    if (showCfarButton) {
      actions.push({
        linkClassName: "mobile-cfar-link",
        content: (
          <>
            <Icon className="cfar-icon" name={IconName.CheckShieldBlue} />
            {textConstants.CANCEL_FOR_ANY_REASON}
          </>
        ),
        onClick: handleOpenCfar,
      });
    } else {
      actions.push({
        content: "Cancel hotel reservation",
        onClick: () => onOpenModal(MyTripsModalTypes.CancelHotel),
      });
    }
  } else if (isCancelled) {
    topActions.push({
      content: (
        <>
          {HOW_REFUNDS_WORK_LINK}
          <Icon className="info-icon" name={IconName.InfoCircle} />
        </>
      ),
      linkClassName: "how-refunds-work-link",
      linkRef: refundPopoverRef,
      onClick: openRefundPopover,
    });
  }

  actions.push({
    content: "Resend confirmation",
    onClick: () => onOpenModal(MyTripsModalTypes.ResendConfirmation),
  });

  if (!isCancelled && itemizedReceiptExp) {
    actions.push({
      content: "View itemized receipt",
      onClick: () => onOpenModal(MyTripsModalTypes.ItemizedHotelReceipt),
    });
  }

  const { reservation, paymentAmountInfo, paymentBreakdown } = hotel;

  const { calculatedTripTotal, refundableAmount } = calculateHotelTotals({
    reservation,
    paymentBreakdown,
    cfar,
  });

  const hotelAddress = reservation.lodgingData?.address as
    | LodgingAddressExact
    | undefined;

  // parse address to only show the street, this could lead to potential bugs
  // because BE returns everything as a full address i.e: "15 Beacon St, Boston, MA, US"
  const hotelStreet = getTwoLineAddresses(hotelAddress).line1;

  const hotelCancellation = (
    <HotelCancellation
      hotelCfar={hotel.ancillaries.cfar}
      cancellationPolicy={hotel.reservation.cancellationPolicy}
      cancellationPolicyV2={hotel.reservation.cancellationPolicyV2}
      refundAmount={refundableAmount}
    />
  );

  return (
    <Box className="mobile-trip-details">
      <Box className="mobile-trip-details-header">
        {!isTravelApp && (
          <FontAwesomeIcon
            icon={faChevronLeft}
            onClick={() => {
              history.goBack();
              setSelectedHotel(null);
            }}
          />
        )}
        <Box className="header-info">
          <Typography variant="h6">{title}</Typography>
          <Typography variant="body2">{nights}</Typography>
        </Box>
      </Box>
      {isCancelled && (
        <NotificationBanner
          className="status-banner"
          icon={<Icon name={IconName.ErrorAlert} />}
          label={textConstants.CANCELED_RES}
          severity={BannerSeverity.ERROR}
        />
      )}
      {topActions.length ? <ActionLinks actions={topActions} /> : null}
      <Box
        className={clsx("mobile-trip-card", "mobile-trip-summary-card", {
          "premier-collection":
            hotel.reservation.lodgingCollection ===
            LodgingCollectionEnum.Premier,
        })}
      >
        {hotel.reservation.lodgingCollection ===
          LodgingCollectionEnum.Premier && (
          <Icon name={IconName.PremierCollectionRibbon} />
        )}
        {reservation.lodgingCollection === LodgingCollectionEnum.Lifestyle && (
          <Icon name={IconName.LifestyleCollectionRibbon} />
        )}

        {isCancelled && (
          <StatusTag
            className="title-status-tag"
            tagInfo={{
              label: textConstants.CANCELED,
              type: TagColors.RED,
            }}
          />
        )}
        <Typography variant="h6" className="trips-title">
          {title}
        </Typography>
        {subtitle && <Typography variant="caption">{subtitle}</Typography>}
        <Box className="info-container">
          <Typography className="label" variant="caption">
            {textConstants.CONFIRMATION}
          </Typography>
          <Typography
            className={clsx("code", { warning: isCancelled })}
            variant="caption"
          >
            {confirmationCode}
          </Typography>
        </Box>
      </Box>
      <SummaryCard className="mobile-trip-card mobile-location-details">
        <Box className="mobile-location-details-container">
          <Box className="mobile-hotel-dates">
            <Typography variant="body1" className="date-label">
              {`${textConstants.CHECK_IN}: `}
            </Typography>
            <Typography variant="body1">{startDate}</Typography>
            <Typography variant="body1">-</Typography>
            <Typography variant="body1" className="date-label">
              {`${textConstants.CHECK_OUT}: `}
            </Typography>
            <Typography variant="body1">{endDate}</Typography>
          </Box>
          <Box className="mobile-hotel-address">
            <Typography variant="body1" className="hotel-address-line">
              {hotelStreet}
            </Typography>
            <Typography variant="caption" className="address-details">
              {`${getPostalCode(reservation)}`}
            </Typography>
            {reservation.lodgingData?.phone && (
              <Typography variant="caption" className="hotel-phone">
                {reservation.lodgingData.phone}
              </Typography>
            )}
          </Box>
          <HotelMap lodging={reservation.lodgingData} hideLabel />
        </Box>
      </SummaryCard>

      {isCorpTenant(config.TENANT) && hotelBookAgainEnabled && (
        <Box style={{ margin: "16px 16px 0 16px" }}>
          <Link
            to={getBookAgainUrl(
              hotel.reservation,
              HotelBookAgainSourceEnum.MyTrips,
            )}
            style={{ textDecoration: "none", display: "block", width: "100%" }}
            aria-label="Book this hotel again"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              trackEvent({
                eventName: HOTEL_BOOK_AGAIN,
                properties: {
                  source: HotelBookAgainSourceEnum.MyTrips,
                },
              });
            }}
          >
            <CorporateButton
              variant="outlined"
              size="large"
              icon={<Icon name={IconName.CorpRedoArrow} />}
              style={{ width: "100%" }}
            >
              Book hotel again
            </CorporateButton>
          </Link>
        </Box>
      )}
      <SummaryCard className="mobile-trip-card mobile-trip-room-type">
        <HotelCardRoomTypeContent bookedRooms={hotel.reservation.bookedRooms} />
      </SummaryCard>
      {hotel.reservation.lodgingCollection ===
        LodgingCollectionEnum.Premier && <PremierCollectionBenefits isMobile />}
      {hotelCancellation && (
        <SummaryCard className="mobile-trip-card cancellation-summary">
          {hotelCancellation}
        </SummaryCard>
      )}
      {hotel.reservation.checkInDate && hotel.reservation.checkOutDate && (
        <SummaryCard className="mobile-trip-card check-in-instructions">
          <HotelCheckInInstructions
            fromDate={hotel.reservation.checkInDate}
            untilDate={hotel.reservation.checkOutDate}
            checkInInstructions={
              hotel.reservation.lodgingData.checkInInstructions
            }
          />
        </SummaryCard>
      )}
      <SummaryCard
        className="mobile-trip-card travellers-summary"
        action={
          <FontAwesomeIcon
            className="mobile-right-chevron"
            onClick={() => onOpenModal(MyTripsModalTypes.TravelersModal)}
            icon={faChevronRight}
          />
        }
      >
        <TravelersSummary
          label="Guest information"
          travelers={reservation.guests
            .map((person) => person?.givenName)
            .join(", ")}
        />
      </SummaryCard>
      {hasAddOnToDisplay ? (
        <SummaryCard className="mobile-trip-card add-ons-summary">
          <AddOnsSummary
            addOns={(() => {
              const addOnItems: AddOn[] = [];
              if (hasCfar) {
                addOnItems.push({
                  expiryDate: cfar?.expired ?? "",
                  expiredTz: cfar?.expiredTz ?? "America/New_York",
                  onClick: handleOpenCfar,
                  isEnabled: !hasCfarExpired(cfar) && !isCancelled,
                  type: AddOnEnum.Cfar,
                });
              }
              return addOnItems;
            })()}
          />
        </SummaryCard>
      ) : null}
      <SummaryCard
        className="mobile-payment-summary mobile-trip-card"
        action={
          <FontAwesomeIcon
            className="mobile-right-chevron"
            onClick={() => onOpenModal(MyTripsModalTypes.PaymentModal)}
            icon={faChevronRight}
          />
        }
      >
        <PaymentSummary
          // TODO: Fix fields when hotels itinerary, payment field is completed
          tripTotalAmount={getCurrencyString({
            currencyCode: calculatedTripTotal.currencyCode,
            currencySymbol: calculatedTripTotal.currencySymbol,
            value: calculatedTripTotal.value,
          })}
          cardLabel={getLabels(paymentAmountInfo).cardLabel}
          cardAmount={getValues(paymentAmountInfo).cardValue}
          rewardsLabel={getLabels(paymentAmountInfo).rewardLabel}
          rewardsAmount={getValues(paymentAmountInfo).rewardValue}
          showCardLabel={Boolean(getLabels(paymentAmountInfo).cardLabel)}
          showRewardsLabel={Boolean(getLabels(paymentAmountInfo).rewardLabel)}
        />
      </SummaryCard>
      <SummaryCard
        className="mobile-trip-card confirmation-summary"
        action={
          hotel.reservation.hotelConfirmationCode &&
          hotel.reservation.hotelConfirmationCode.HotelConfirmationCode ===
            HotelConfirmationCodeEnum.HotelConfirmationConfirmed ? (
            <FontAwesomeIcon
              className="mobile-right-chevron"
              onClick={() => onOpenModal(MyTripsModalTypes.ConfirmationModal)}
              icon={faChevronRight}
            />
          ) : null
        }
      >
        <ConfirmationSummary confirmationCode={confirmationCode} />
      </SummaryCard>
      {isExpenseManagementEnabled && tripDescription && (
        <SummaryCard className="mobile-trip-card trip-description-summary">
          <TripDescriptionSummary tripDescription={tripDescription} />
        </SummaryCard>
      )}
      <MobileActionLinks actions={actions} />
      {isCancelled && (
        <Popover
          anchorEl={refundPopoverRef.current}
          anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
          className="how-refunds-work-popover"
          onClose={closeRefundPopover}
          open={refundPopoverOpen}
        >
          <Typography className="subtitle">{HOW_REFUNDS_WORK_TEXT}</Typography>
        </Popover>
      )}
    </Box>
  );
};
